import { lazy, useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useNavigate,
  useLocation,
  matchPath,
  generatePath,
} from 'react-router-dom';

import { OAuth2Service } from 'core/services/auth/oauth2.service';
import { ActivitySessionService } from 'core/services/session/activitySession.service';
import { CurrentUserService } from 'core/services/auth/currentUser.service';
import { StatsService } from 'core/services/stats.service';
import ScormAPI from "utils/scormWrapper.util";

import {
  getUrlQueryParamValue,
  getParams,
  getAuthParamValue,
} from 'utils/url.util';
import { checkIsTokenExpired } from 'utils/token.util';
import { logError } from 'utils/environment.util';
import { generateNavigationMap } from 'utils/generateNavigationMap.util';

import { setAuthStatus } from 'store/authentication.slice';
import { setCurrentUser } from 'store/user.slice';
import { setEvaluations } from 'store/evaluations.slice';
import { setNavigationMap } from 'store/navigationMap.slice';
import {
  setActivitySession,
  setActivitySessionStatus,
} from 'store/activitySession.slice';

import { getAuthStatus, getActivitySessionStatus } from 'selectors';
import { AUTH_STATUS, ACTIVITY_SESSION_STATUS, QUIZ_ERROR } from 'constants';
import { routes } from 'router';

const Go = lazy(() => import('components/pages/Go/Go'));
const Result = lazy(() => import('components/pages/Result/Result'));
const Player = lazy(() => import('components/pages/Player/Player'));
const ResultGuard = lazy(() => import('components/pages/Result/ResultGuard'));

let sentScore = false;

const PrivateRoute = (props) => {
  const [isLogged, setIsLogged] = useState(false);
  const [isOpenSession, setIsOpenSession] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const dispatch = useDispatch();
  const activitySession = useSelector((state) => state.activitySession);
  const isEnding = useSelector((state) => state.navigationMap.isEnding);
  const navigationMap = useSelector(
    (state) => state.navigationMap.navigationMap,
  );
  const activitySessionStatus = useSelector(getActivitySessionStatus);
  const authStatus = useSelector(getAuthStatus);

  const navigate = useNavigate();
  const { pathname, search: searchQueryString } = useLocation();


  const [env, activitySessionId, authenticated, activityItemId] =
    useMemo(() => {
      const {
        env: paramsEnv,
        activitySessionId: paramsActivitySessionId,
        authenticated: paramsAuthenticated,
        templateId: paramsTemplateId,
        variantId: paramsVariantId,
        activityItemId,
      } = getParams(pathname);

      const env =
        paramsEnv ||
        sessionStorage.getItem('env') ||
        getAuthParamValue(searchQueryString, 'env');
      const activitySessionId =
        paramsActivitySessionId ||
        getAuthParamValue(searchQueryString, 'activitySessionId');
      let authenticated =
        paramsAuthenticated ||
        getAuthParamValue(searchQueryString, 'authenticated');
      const templateId =
        paramsTemplateId || getAuthParamValue(searchQueryString, 'templateId');
      const variantId =
        paramsVariantId || getAuthParamValue(searchQueryString, 'variantId');

      if (!authenticated) {
        authenticated = 'True';
      }
      if (templateId) {
        sessionStorage.setItem('templateId', templateId);
      }
      if (variantId) {
        sessionStorage.setItem('variantId', variantId);
      }

      return [env, activitySessionId, authenticated, activityItemId];
    }, [pathname, searchQueryString]);

  const token = sessionStorage.getItem('token');
  const code = getUrlQueryParamValue(searchQueryString, 'code');
  const oauth2Service = useMemo(() => new OAuth2Service(env), [env]);
  const statsService = new StatsService(env, token);
  const activitySessionService = new ActivitySessionService(env, token);

  const authUser = useCallback(() => {
    sessionStorage.setItem('env', env);
    oauth2Service.authorize(
      window.location.origin,
      encodeURIComponent(
        `activitySessionId=${activitySessionId}&authenticated=${authenticated}&env=${env}`,
      ),
    );
  }, [activitySessionId, authenticated, env, oauth2Service]);

  const getAuth = useCallback(() => {
    async function check() {
      if (!code) return;

      try {
        const { accessToken, refreshToken } = await oauth2Service.getAuth(code);
        setIsLogged(true);
        sessionStorage.setItem('token', accessToken);
        sessionStorage.setItem('refreshToken', refreshToken);
        dispatch(setAuthStatus({ status: AUTH_STATUS.DONE }));
      } catch (error) {
        dispatch(setAuthStatus({ status: AUTH_STATUS.FAILED }));
        logError(error);
      }
    }
    check();
  }, [dispatch, oauth2Service, code]);

  const updateSessionData = useCallback(async () => {
    dispatch(
      setActivitySessionStatus({
        status: ACTIVITY_SESSION_STATUS.IN_PROGRESS,
      }),
    );

    try {
      let activitySession;
      if (
        authStatus === AUTH_STATUS.OPEN_SESSION ||
        authenticated === 'False'
      ) {
        activitySession =
          await activitySessionService.getActivityOpenSession(
            activitySessionId,
          );
      } else {
        activitySession =
          await activitySessionService.getActivitySession(activitySessionId);
      }
      sessionStorage.setItem('activitySessionId', activitySessionId);
      dispatch(
        setNavigationMap(
          generateNavigationMap(activitySession?.activity?.items),
        ),
      );
      dispatch(setActivitySession(activitySession));
      dispatch(setEvaluations(activitySession));
      dispatch(
        setActivitySessionStatus({
          status: ACTIVITY_SESSION_STATUS.SESSION_RECEIVED,
        }),
      );

      const playerPathMatch = matchPath(
        {
          path: routes.player.path,
          exact: false,
        },
        pathname,
      );
      const resultPathMatch = matchPath(
        {
          path: routes.result.path,
          exact: false,
        },
        pathname,
      );

      if (!playerPathMatch && !resultPathMatch) {
        const isModeStandalone =
          process.env.REACT_APP_MODE_VAR === 'standalone';

        navigate(
          generatePath(routes.player.path, {
            env: isModeStandalone ? 'ALL' : env,
            activitySessionId: isModeStandalone ? '0' : activitySessionId,
            authenticated: isModeStandalone ? 'False' : authenticated,
            activityItemId: isModeStandalone
              ? standalone.items[0].item.id
              : activityItemId,
          }),
          {
            activitySessionStatus: ACTIVITY_SESSION_STATUS.SESSION_RECEIVED,
          },
        );
      }
    } catch (error) {
      dispatch(
        setActivitySessionStatus({ status: ACTIVITY_SESSION_STATUS.FAILED }),
      );
      logError(error);
    }
  }, [
    authStatus,
    authenticated,
    token,
    activitySessionId,
    env,
    dispatch,
    navigate,
    pathname,
    activityItemId,
  ]);

  const updateCurrentUser = useCallback(async () => {
    const currentUserService = new CurrentUserService(env, token);

    try {
      const currentUser = await currentUserService.getCurrentUser();

      dispatch(
        setCurrentUser({
          IdUser: currentUser.IdUser,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          isTeacher: currentUser.isTeacher,
        }),
      );
    } catch (error) {
      logError(error);
    }
  }, [dispatch, env, token]);

  useEffect(() => {
    setIsStandalone(process.env.REACT_APP_MODE_VAR === 'standalone');
  }, []);

  useEffect(() => {
    if (activitySessionStatus != ACTIVITY_SESSION_STATUS.SESSION_RECEIVED) {
      dispatch(
        setActivitySessionStatus({ status: ACTIVITY_SESSION_STATUS.NONE }),
      );
    } else {
      setIsLogged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!code) {
      dispatch(setAuthStatus({ status: AUTH_STATUS.NONE }));
    }
  }, [dispatch, code]);

  useEffect(() => {
    if (authenticated === 'False') {
      dispatch(setAuthStatus({ status: AUTH_STATUS.OPEN_SESSION }));
    }
  }, [authenticated, dispatch]);

  const exitCallback = useCallback(() => {
    statsService.PlayerExiting(activitySessionId);
  }, [activitySessionId]);

  useEffect(() => {
    if (authStatus === AUTH_STATUS.OPEN_SESSION || authenticated === 'False') {
      setIsOpenSession(true);
      sessionStorage.setItem('token', '');
    }

    if (
      activitySessionStatus === ACTIVITY_SESSION_STATUS.SESSION_RECEIVED &&
      !isStandalone
    ) {
      // Only notify the API if the session is performed in a capsule/course/group session
      if (
        activitySession !== null &&
        activitySession.activity.initialItem != null &&
        typeof activitySessionId === 'string'
      ) {
        statsService.PlayerInitialized(
          activitySessionId,
          activitySession.resumeSession,
        );

        window.addEventListener('beforeunload', exitCallback);
        window.addEventListener('unload', exitCallback);
      }
    }

    if (
      (!authStatus === AUTH_STATUS.OPEN_SESSION &&
        !authenticated === 'False' &&
        authStatus === AUTH_STATUS.IN_PROGRESS) ||
      authStatus === AUTH_STATUS.FAILED ||
      activitySessionStatus === ACTIVITY_SESSION_STATUS.SESSION_RECEIVED ||
      activitySessionStatus === ACTIVITY_SESSION_STATUS.FAILED
    ) {
      return;
    }

    if (!(activitySessionId && env) && !isStandalone) {
      return setIsLogged(false);
    }

    const isExpired = checkIsTokenExpired(token);
    const isLogged =
      !isExpired &&
      (authStatus === AUTH_STATUS.DONE || authStatus === AUTH_STATUS.NONE);

    setIsLogged(isLogged);

    if (code && authStatus !== AUTH_STATUS.DONE) {
      return getAuth();
    }
    if (
      !isLogged &&
      authStatus !== AUTH_STATUS.OPEN_SESSION &&
      authenticated != 'False' &&
      process.env.REACT_APP_MODE_VAR !== 'standalone'
    ) {
      return authUser();
    }

    if (activitySessionStatus !== ACTIVITY_SESSION_STATUS.IN_PROGRESS) {
      updateSessionData();
      if (
        authStatus !== AUTH_STATUS.OPEN_SESSION &&
        authenticated != 'False' &&
        process.env.REACT_APP_MODE_VAR !== 'standalone'
      ) {
        updateCurrentUser();
      }
    }
  }, [
    authStatus,
    isOpenSession,
    token,
    authUser,
    updateSessionData,
    updateCurrentUser,
    getAuth,
    code,
    activitySessionStatus,
    env,
    isStandalone,
    activitySessionId,
    authenticated,
  ]);

  const preloader = document.getElementById('preloader');
  if (preloader) preloader.style.display = 'none';

  if(isEnding && !sentScore){
    ScormAPI.setCompletionStatus("completed");
    sentScore = true;
  }

  const ScormComponent =
    isEnding && window.location.pathname.includes('result')
      ? ResultGuard
      : Player;

  return navigationMap !== QUIZ_ERROR.ITEM_NOT_FOUND ? (
    isLogged || isOpenSession ? (
      <props.component />
    ) : isStandalone ? (
      <ScormComponent />
    ) : (
      <Go />
    )
  ) : (
    <Result />
  );
};

export default PrivateRoute;
